import { c as _c } from "react/compiler-runtime";
import { usePartnersQuery } from '@/api/skippay/partners';
import { usePartnersCategoriesQuery } from '@/api/skippay/partnersCategories';
import { Partner, PartnerCategories, Partners } from '@/types/partners';
import { QueryFilter } from '@/utils/query';
import { InfiniteData, UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { SELECT_ALL_VALUE } from './searchParamParsers';
import { logEvent } from '@/analytics/events';
import { components } from '@/api/skippay/generated/selfCare';
import { useMemo } from 'react';
import { getCountryCode } from '@/constants/country';
const DEFAULT_PAGE_LIMIT = 12;
export const FIELD_PAYMENT_METHOD = 'paymentMethod';
const FIELD_TARGET_COUNTRY = 'targetCountry';
export const PAYMENT_METHOD_ALL = 'all';
export const PAYMENT_METHOD_PAY_IN_THREE = 'payInThree';
export const PAYMENT_METHOD_DEFERRED_PAYMENT = 'deferredPayment';
const TARGET_COUNTRY = getCountryCode(process.env.NEXT_PUBLIC_LANGUAGE);
export type PaymentMethodFieldValues = typeof PAYMENT_METHOD_ALL | typeof PAYMENT_METHOD_PAY_IN_THREE | typeof PAYMENT_METHOD_DEFERRED_PAYMENT;
export type SearchFormValues = {
  fulltext?: string;
  categoryId?: string;
  paymentMethod?: PaymentMethodFieldValues;
};
export const FIELD_FULLTEXT = 'fulltext';
export const FIELD_CATEGORY = 'categoryId';

// For backward compatibility with old payment methods remap them to new ones
export function remapPaymentMethod(paymentMethod: string): components['schemas']['Partner']['paymentMethod'] {
  switch (paymentMethod) {
    case PAYMENT_METHOD_ALL:
      return 'ALL';
    case PAYMENT_METHOD_PAY_IN_THREE:
      return 'PAY_IN_THREE';
    case PAYMENT_METHOD_DEFERRED_PAYMENT:
      return 'DEFERRED_PAYMENT';
    default:
      return 'ALL';
  }
}
const logViewPromotionEvent = (eshops: Partner[] | undefined, section: string, offset: number = 1, totalCount: number): void => {
  if (!eshops) {
    return;
  }
  const items = eshops?.map((eshop, index) => ({
    promotion_id: 'affil_promo',
    promotion_name: eshop?.name,
    creative_name: eshop?.name,
    creative_slot: `${section}_${index + offset}`,
    location_id: eshop?.url
  }));
  logEvent('view_promotion', {
    search_results: totalCount,
    ecommerce: {
      items
    }
  });
};
const getQueryFilter = (formValues: SearchFormValues): string => {
  let partnersFilter = new QueryFilter().prop(FIELD_TARGET_COUNTRY).eq(TARGET_COUNTRY);
  Object.keys(formValues).forEach(key => {
    if (key === FIELD_FULLTEXT && formValues[FIELD_FULLTEXT] !== '') {
      partnersFilter = partnersFilter.prop(FIELD_FULLTEXT).fulltext(formValues[FIELD_FULLTEXT] as string);
    }
    if (key === FIELD_CATEGORY && formValues.categoryId !== SELECT_ALL_VALUE) {
      partnersFilter = partnersFilter.prop(FIELD_CATEGORY).eq(formValues[FIELD_CATEGORY] as string);
    }
    if (key === FIELD_PAYMENT_METHOD) {
      const method = formValues[FIELD_PAYMENT_METHOD] as string;
      partnersFilter = partnersFilter.prop(FIELD_PAYMENT_METHOD).eq(remapPaymentMethod(method));
    }
  });
  return partnersFilter.toString();
};
export function getBaseFilter(baseFilter: QueryFilter): string {
  return baseFilter.prop(FIELD_TARGET_COUNTRY).eq(TARGET_COUNTRY).prop(FIELD_PAYMENT_METHOD).eq(remapPaymentMethod(PAYMENT_METHOD_ALL)).toString();
}
const useWhereToShop = t0 => {
  const $ = _c(28);
  const {
    formValues,
    initialPartnersData
  } = t0;
  const [queryFilter, setQueryFilter] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const allPartnersPreviousData = useRef(t1);
  let t2;
  if ($[1] !== formValues) {
    t2 = getQueryFilter(formValues);
    $[1] = formValues;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const partnersFilter = t2;
  let t3;
  let t4;
  if ($[3] !== partnersFilter) {
    t3 = () => {
      allPartnersPreviousData.current = [];
      setQueryFilter(partnersFilter);
    };
    t4 = [partnersFilter, allPartnersPreviousData, setQueryFilter];
    $[3] = partnersFilter;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    const basePartnersFilter = new QueryFilter();
    t5 = getBaseFilter(basePartnersFilter);
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const isAllPaymentFilter = t5;
  let t6;
  if ($[7] !== formValues || $[8] !== queryFilter) {
    t6 = queryFilter === isAllPaymentFilter && (!formValues.paymentMethod || formValues.paymentMethod as string === remapPaymentMethod(PAYMENT_METHOD_ALL));
    $[7] = formValues;
    $[8] = queryFilter;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const isSearchingFilter = t6;
  const isSearching = queryFilter !== "" && !isSearchingFilter;
  const t7 = isSearching ? queryFilter : isAllPaymentFilter;
  let t8;
  if ($[10] !== t7) {
    t8 = {
      limit: DEFAULT_PAGE_LIMIT,
      filter: t7
    };
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = !isSearching && initialPartnersData ? initialPartnersData : undefined;
  let t10;
  if ($[12] !== t8 || $[13] !== t9) {
    t10 = {
      searchParams: t8,
      initialData: t9
    };
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const allPartners = usePartnersQuery(t10);
  const categories = usePartnersCategoriesQuery();
  let t11;
  if ($[15] !== allPartners.data?.pages || $[16] !== allPartners.isFetching || $[17] !== isSearching) {
    t11 = () => {
      if (isSearching && allPartners.data?.pages?.[0]?.data?.length === 0) {
        logEvent("no_results", {
          action: "search",
          search_term: window?.location.search
        });
      } else {
        const allFetchedPartners = allPartners.data?.pages?.flatMap(_temp);
        const difference = allFetchedPartners?.filter(partner => !allPartnersPreviousData.current.some(p => p.id === partner.id));
        if (!allPartners.isFetching && difference && difference.length > 0) {
          logViewPromotionEvent(difference, isSearching ? "searched" : "all", (allPartnersPreviousData?.current?.length || 0) + 1, allPartners?.data?.pages[0]?.pagingInfo?.totalCount || 0);
          allPartnersPreviousData.current = [...allPartnersPreviousData.current, ...difference];
        }
      }
    };
    $[15] = allPartners.data?.pages;
    $[16] = allPartners.isFetching;
    $[17] = isSearching;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = allPartners.data?.pages;
  let t13;
  if ($[19] !== allPartners.isFetching || $[20] !== isSearching || $[21] !== t12) {
    t13 = [t12, isSearching, allPartners.isFetching];
    $[19] = allPartners.isFetching;
    $[20] = isSearching;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  useEffect(t11, t13);
  const loading = [allPartners, categories].some(_temp2);
  const t14 = isSearching ? "searched" : "all";
  let t15;
  if ($[23] !== allPartners || $[24] !== categories || $[25] !== loading || $[26] !== t14) {
    t15 = {
      categories,
      allPartners,
      section: t14,
      loading
    };
    $[23] = allPartners;
    $[24] = categories;
    $[25] = loading;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  return t15;
};
export default useWhereToShop;
function _temp(subArray) {
  return subArray.data;
}
function _temp2(t0) {
  const {
    isPending
  } = t0;
  return isPending;
}